import * as React from "react";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { GuestLoginAdminModel } from "../../../user/guestLogin";
import { RoleRestricted, Roles } from "../../../user";
import { QESButton } from "../../../shared/qes-standards/Button";
import { GuestLoginAdminPanelContent } from "../GuestLoginAdminPanelContent";
import { AdminUsersContext } from "../../contexts/AdminUsersContext";
import { orderBy, some } from "lodash";
import { RoleGroupModel, RoleGroupService } from "@app/admin/role-groups";
import { FlyoutLink } from "@app/shared/Flyouts";
import { JSX } from "react";

interface GuestLoginsProps {
  disabled?: boolean;
}

interface GuestLoginTableData {
  clientName: JSX.Element;
  roleGroups: string;
  roles: string;
  [wafflesKeyColumnName]: string;
}

const columnConfigBase: ColumnConfig = {
  clientName: {
    displayText: "Client",
    testId: "client-name",
  },
  roleGroups: {
    displayText: "Role Groups",
    hidden: false,
    minWidth: "300px",
    noSort: true,
    testId: "roleGroups",
    wrapContent: true,
  },
  roles: {
    displayText: "Roles",
    minWidth: "300px",
    noSort: true,
    testId: "roles",
    wrapContent: true,
  },
};

export const GuestLogins: React.FC<React.PropsWithChildren<GuestLoginsProps>> = (props: GuestLoginsProps) => {

  const adminUsersContext = React.useContext(AdminUsersContext);

  const [guestLoginTableData, setGuestLoginTableData] = React.useState<GuestLoginTableData[]>([]);
  const [listStateEnabled, setListStateEnabled] = React.useState(true);
  const [roleGroups, setRoleGroups] = React.useState<RoleGroupModel[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [columnConfig, setColumnConfig] = React.useState<ColumnConfig>({});

  const buildTableData = (data: GuestLoginAdminModel[]/*, clearFilter?: boolean*/): GuestLoginTableData[] => {
    const filteredData = data;

    return filteredData.map<GuestLoginTableData>((guestLogin) => {
      /* eslint-disable sort-keys */
      return {
        clientName: listStateEnabled ? (
          <FlyoutLink
            classNames="link-text-default"
            content={{
              body: <GuestLoginAdminPanelContent guestUserId={guestLogin.guestUserId} guestClientId={guestLogin.clientId} guestClientName={guestLogin.clientName} guestClientAssignedRoles={guestLogin.roleNames} guestClientAssignedRoleGroups={guestLogin.roleGroupIds} />,
              title: "Edit Guest Login",
            }}
            disabled={props.disabled}
            testId="guest-logins__edit-guest-login-flyout"
          >
            {guestLogin.clientName}
          </FlyoutLink>
        ) : (
          <div>{guestLogin.clientName}</div>
        ),
        roles: guestLogin.roleNames.join(", "),
        roleGroups: roleGroups.filter(x => guestLogin.roleGroupIds.includes(x.roleGroupId)).map(x => x.name).join(", "),
        [wafflesKeyColumnName]: guestLogin.clientId.toString(),
      };
      /* eslint-enable sort-keys */
    });
  }

  const handleCheckboxChange = ((selectedKeys: string[]) => {
    adminUsersContext.setSelectedClients(selectedKeys);
  });

  const handleDelete = () => adminUsersContext.deleteGuestLogins();

  const handleUndelete = () => adminUsersContext.reactivateDeletedGuestLogins();

  const toggleActiveDeletedState = () => setListStateEnabled(!listStateEnabled);

  React.useEffect(() => {
    initializePage();
  }, []);

  React.useEffect(() => setGuestLoginTableData(buildTableData(listStateEnabled ? adminUsersContext.guestLogins : adminUsersContext.deletedGuestLogins)), [adminUsersContext.guestLogins, adminUsersContext.deletedGuestLogins, listStateEnabled, roleGroups]);

  const customSort = (rows: GuestLoginTableData[], column: keyof GuestLoginTableData, direction: SortDirections) => {
    if (column !== "clientName") {
      return rows;
    }
    return orderBy(rows, (r) => r.clientName.key, direction === SortDirections.Asc ? "asc" : "desc");
  }

  const initializePage = async () => {
    setLoading(true);

    const roleGroupsService = new RoleGroupService();
    setRoleGroups((await roleGroupsService.getAllRoleGroups(true, false, false, false, 0, -1)).roleGroupList);
    setColumnConfig(columnConfigBase);
    setLoading(false);
  }

  return (
    <>
      <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN]}>
        <FlyoutLink
          content={{
            body: <GuestLoginAdminPanelContent />,
            title: "Add Guest Login",
          }}
          disabled={props.disabled || !listStateEnabled}
          classNames="mr-3 mb-3 qes-button-primary"
          testId="guest-logins__add-guest-login-flyout"
        >
          Add Guest Login
        </FlyoutLink>
        {listStateEnabled &&
          <QESButton qesTestId="guestlogins-delete" qesType="secondary-danger" disabled={!some(adminUsersContext.selectedClients)} onClick={handleDelete}>
            Delete Selected
          </QESButton>}
        {!listStateEnabled &&
          <QESButton qesTestId="guestlogins-undelete" qesType="secondary" disabled={!some(adminUsersContext.selectedClients)} onClick={handleUndelete}>
            Restore Selected
          </QESButton>}
      </RoleRestricted>
      <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN]}>
        <QESButton className="mb-3 pull-right" qesTestId="guestlogins-active-delete-toggle" qesType="secondary" onClick={toggleActiveDeletedState}>
          {listStateEnabled ? "Switch to Deleted" : "Switch to Active"}
        </QESButton>
      </RoleRestricted>
      <Waffles<GuestLoginTableData>
        columns={columnConfig}
        customSort={customSort}
        data={guestLoginTableData}
        emptyTableMessage="There are no Guest Logins for this user."
        fullWidth={true}
        testId="guestlogins-table"
        selectedKeys={adminUsersContext.selectedClients ?? []}
        showCheckboxes={true}
        handleCheckboxChange={handleCheckboxChange}
        loading={loading} />
    </>
  );
}
